import { AlertCircle, FileText, Target, Users } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Checkbox } from '../../ui/checkbox';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Textarea } from '../../ui/textarea';

export function AdditionalDetails() {
  const [priorities, setPriorities] = useState<string[]>([]);

  const businessPriorities = [
    'Cost Reduction',
    'Process Automation',
    'Cash Flow Optimization',
    'Risk Management',
    'Digital Transformation',
    'Compliance',
    'Scalability',
    'Vendor Management',
  ];

  const togglePriority = (priority: string) => {
    setPriorities((prev) =>
      prev.includes(priority) ? prev.filter((p) => p !== priority) : [...prev, priority]
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            Business Priorities
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Select the key business priorities for this prospect
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {businessPriorities.map((priority) => {
              const id = `priority-${priority.toLowerCase().replace(/\s+/g, '-')}`;
              return (
                <label
                  key={priority}
                  htmlFor={id}
                  className={`flex items-center space-x-2 p-3 border transition-colors cursor-pointer ${
                    priorities.includes(priority)
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <Checkbox
                    id={id}
                    checked={priorities.includes(priority)}
                    onChange={() => togglePriority(priority)}
                  />
                  <span className="text-sm font-medium">{priority}</span>
                </label>
              );
            })}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Decision Making Process
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="decisionMakers">Key Decision Makers</Label>
              <Textarea
                id="decisionMakers"
                placeholder="List the key people involved in decision making..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="decisionProcess">Decision Timeline</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select timeline" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediate">Immediate (1-2 weeks)</SelectItem>
                  <SelectItem value="short">Short-term (1-3 months)</SelectItem>
                  <SelectItem value="medium">Medium-term (3-6 months)</SelectItem>
                  <SelectItem value="long">Long-term (6+ months)</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="budget">Budget Range</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select budget range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="under-50k">Under $50K</SelectItem>
                  <SelectItem value="50k-100k">$50K - $100K</SelectItem>
                  <SelectItem value="100k-250k">$100K - $250K</SelectItem>
                  <SelectItem value="250k-500k">$250K - $500K</SelectItem>
                  <SelectItem value="over-500k">Over $500K</SelectItem>
                  <SelectItem value="tbd">To be determined</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="competition">Known Competitors</Label>
              <Input id="competition" placeholder="e.g., Bill.com, AvidXchange, Coupa" />
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            Risk Assessment
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="riskLevel">Overall Risk Level</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Assess risk level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="bg-green-100 text-green-800">
                        Low
                      </Badge>
                      <span>Low Risk</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="medium">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                        Medium
                      </Badge>
                      <span>Medium Risk</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="high">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="bg-red-100 text-red-800">
                        High
                      </Badge>
                      <span>High Risk</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="compliance">Compliance Requirements</Label>
              <Input id="compliance" placeholder="e.g., SOX, PCI-DSS, GDPR" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="riskFactors">Risk Factors & Concerns</Label>
            <Textarea
              id="riskFactors"
              placeholder="Describe any potential risks or concerns..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Additional Notes
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="notes">General Notes</Label>
            <Textarea
              id="notes"
              placeholder="Any additional information, observations, or important details about this prospect..."
              rows={4}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="nextSteps">Proposed Next Steps</Label>
            <Textarea
              id="nextSteps"
              placeholder="Outline the recommended next steps for this prospect..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
